import { AssignmentIcon, Button, Icon, Tag, Spinner } from "components";
import TrashIcon from "assets/icons/trash.svg";
import EditIcon from "assets/icons/pen-to-square.svg";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser, useCopyTemplate } from "utils/hooks";
import { Group } from "types";
import { obfuscateEmail, truncateText } from "utils/helpers";
import ClampLines from "react-clamp-lines";

interface AssignmentLibaryCardProps {
  isDraft: boolean;
  template: any;
  patientHasAssignment: boolean;
  patient: any;
  handleAssignmentButtonClick: (type: any) => void;
  slug?: string;
  handleAssignmentDeleteActionClick: (assignment: any) => void;
  group: Group;
  isDisplayedInModal: boolean;
  handleShowAssignmentPreview?: () => void;
  selected: boolean;
}

export const AssignmentLibraryCard = ({
  template,
  patientHasAssignment,
  patient,
  handleAssignmentButtonClick,
  handleAssignmentDeleteActionClick,
  isDraft,
  group,
  isDisplayedInModal,
  handleShowAssignmentPreview,
  selected,
}: AssignmentLibaryCardProps) => {
  const history = useNavigate();
  const { user, isTemplateMaster } = useCurrentUser();
  const { copyTemplate, copying } = useCopyTemplate();
  const handleNavigateToDraft = (assignmentId: string) => {
    history(`/builder/${assignmentId}`);
  };

  const canEdit =
    template.authorId === user?.id ||
    ((template.authorId === process.env.REACT_APP_TEMPLATE_INTERN_ID_1 ||
      template.authorId === process.env.REACT_APP_TEMPLATE_INTERN_ID_1) &&
      user?.id === process.env.REACT_APP_TEMPLATE_MASTER_ID);

  const canCopyTemplate =
    template?.category !== "ASSESSMENT" ||
    (template?.category === "ASSESSMENT" && isTemplateMaster);

  const modalityTags = template?.tags?.filter(
    (tag: any) => tag.type === "modality"
  );

  const handleAssignmentClick = () => {
    handleAssignmentButtonClick(template);
    handleShowAssignmentPreview?.();
  };

  const handleCopyTemplate = (id: string) => {
    if (copying) return;
    copyTemplate(id);
  };

  return (
    <div
      className={`assignment-resource-library-card ${
        isDisplayedInModal
          ? "assignment-resource-library-card__bottom-modal"
          : ""
      } ${isDisplayedInModal && selected ? "selected" : ""}`}
      id={template.uuid}
      {...(isDisplayedInModal && {
        onClick: handleAssignmentClick,
        onKeyDown: handleAssignmentClick,
        role: "button",
        tabIndex: 0,
      })}
    >
      <div className="assignment-resource-library-card__inner">
        <div className="assignment-resource-library-card-line">
          <div className="flex align-start">
            <div className="assignment-resource-library-card-top-line">
              <AssignmentIcon
                icon={template.icon?.src || template.icon}
                style={template.type}
                color={template.color}
              />
            </div>
            <div className="assignment-resource-library-card-title-box">
              <div
                className={`assignment-resource-library-card-title ${
                  isDraft ? "assignment-resource-library-card-title__draft" : ""
                }`}
              >
                {template.title}
              </div>
              {template.tags && (
                <div className="assignment-libary-card_tags">
                  {modalityTags?.map((t: any) => {
                    return (
                      <Tag
                        key={t.uuid}
                        title={t.title}
                        color={t.color}
                        type="tiny"
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="assignment-resource-library-description">
          {isDisplayedInModal ? (
            <ClampLines
              text={template.description}
              id="resource-clamped-title"
              lines={3}
              ellipsis="..."
              innerElement="div"
              buttons={false}
            />
          ) : (
            template.description
          )}
        </div>
        <div className="assignment-libary-card-actions">
          <div className="assignment-resource-library-card__actions">
            {canEdit && !isDisplayedInModal && (
              <span
                className="assignment-resource-library-card__actions-action"
                onClick={() => handleNavigateToDraft(template.slug)}
                onKeyDown={() => handleNavigateToDraft(template.slug)}
                role="button"
                tabIndex={0}
              >
                <Icon src={EditIcon} />
              </span>
            )}
            {canCopyTemplate && (
              <span
                className="assignment-resource-library-card__actions-action"
                onClick={() => handleCopyTemplate(template.uuid)}
                onKeyDown={() => handleCopyTemplate(template.slug)}
                role="button"
                tabIndex={0}
              >
                {copying ? <Spinner isActive={true} /> : <Icon src={"Copy3"} />}
              </span>
            )}
            {canEdit && !isDisplayedInModal && (
              <span
                className="assignment-resource-library-card__actions-action"
                onClick={() => handleAssignmentDeleteActionClick(template)}
                onKeyDown={() => handleAssignmentDeleteActionClick(template)}
                role="button"
                tabIndex={0}
              >
                <Icon src={TrashIcon} />
              </span>
            )}
          </div>

          {patientHasAssignment && (
            <p className="assignment-resource-library-in-use">
              {`Assigned to ${truncateText(
                group ? group?.name : patient?.firstName ?? patient.email
              )}`}
            </p>
          )}
          <div className="assignment-libary-card_preview">
            {isDraft ? (
              <span className="uppercase light-text draft-tag">
                <i>Draft</i>
              </span>
            ) : (
              <Button
                name="add_assignment_button"
                value="Preview"
                extraClass="primary pill"
                onClick={handleAssignmentClick}
              />
            )}
          </div>
        </div>
        {isTemplateMaster && !canEdit && template.authorEmail && (
          <div className="mt-4">
            <Tag
              title={obfuscateEmail(template?.authorEmail, 5)}
              color="#003366"
            />
          </div>
        )}
      </div>
    </div>
  );
};
